// src/features/admin/RadarSignalNoisePanel.jsx
//
// P-51: Admin "Radar — Signal & noise" panel.
//
// Renders four config cards:
//   1. Live preview banner — feed counts from the current saved config.
//   2. Emitters card — on/off toggles per emitter; locked toggles for
//      trust-gated (utilisation_signals) and deferred (cost_anomaly).
//   3. Timing & return dials — six stepper controls for lead times and
//      re-arm windows.
//   4. Ranking weights — five draggable sliders; auto-normalize to 100.
//   5. Bands & confidence gate — act-now/this-cycle cutoffs and low-conf cap.
//
// Footer: "Save changes" PATCHes dirty fields; "Reset to defaults" restores.

import React, { useEffect, useState, useCallback, useRef } from "react";
import { T } from "../../app/tokens.js";
import { getRadarSettings, patchRadarSettings } from "../../data/radarClient.js";
import { getFocusItemsFeed }                    from "../../data/focusItemsClient.js";
import { showToast }                            from "../../components/toastService.jsx";
import { log }                                  from "../../utils/logger.js";

// ─── Defaults (mirror server RADAR_DEFAULTS) ──────────────────────────────────

const DEFAULT_TIMING = {
  lease_window_lead_months:   12,
  notice_window_lead_days:    90,
  holdover_recheck_days:      14,
  data_staleness_days:        35,
  overcapacity_realert_delta: 10,
  signal_rearm_days:          60,
};

const DEFAULT_EMITTERS = {
  lease_decision_windows: true,
  plan_coverage_gaps:     true,
  review_cadence:         true,
  data_quality_flags:     true,
  over_capacity:          true,
  utilisation_signals:    false,
  cost_anomaly:           false,
};

const DEFAULT_RANKING_WEIGHTS = {
  deadline:        30,
  consequence:     30,
  reversibility:   15,
  blocking:        15,
  data_confidence: 10,
};

const DEFAULT_BANDS = {
  act_now_days:       30,
  this_cycle_days:    90,
  low_confidence_cap: true,
};

const DEFAULT_CONFIG = {
  timing:         DEFAULT_TIMING,
  emitters:       DEFAULT_EMITTERS,
  rankingWeights: DEFAULT_RANKING_WEIGHTS,
  bands:          DEFAULT_BANDS,
};

// ─── Emitter metadata ─────────────────────────────────────────────────────────

const EMITTER_META = [
  { key: "lease_decision_windows", label: "Lease decision windows",  desc: "expiry · notice · holdover",                      status: "live" },
  { key: "plan_coverage_gaps",     label: "Plan-coverage gaps",      desc: "markets without a complete plan",                  status: "live" },
  { key: "review_cadence",         label: "Review cadence",          desc: "reviews overdue vs cadence",                       status: "live" },
  { key: "data_quality_flags",     label: "Data-quality flags",      desc: "vacated-HC · missing rent · staleness",            status: "live" },
  { key: "over_capacity",          label: "Over-capacity",           desc: "assignment > 100%",                                status: "live" },
  { key: "utilisation_signals",    label: "Utilisation signals",     desc: "badge-based; unlocks when the badge feed is trusted", status: "gated" },
  { key: "cost_anomaly",           label: "Cost anomaly",            desc: "opex variance; finance source pending",            status: "deferred" },
];

const TIMING_META = [
  { key: "lease_window_lead_months",   label: "Lease-window lead",          desc: "how early a lease decision appears before its window", unit: "months", min: 1 },
  { key: "notice_window_lead_days",    label: "Notice-window lead",         desc: "how early a serve-notice deadline appears",             unit: "days",   min: 1 },
  { key: "holdover_recheck_days",      label: "Holdover re-check",          desc: "how often a holdover re-nags until fixed",              unit: "days",   min: 1 },
  { key: "data_staleness_days",        label: "Data staleness",             desc: "age before headcount/data flags as stale",              unit: "days",   min: 1 },
  { key: "overcapacity_realert_delta", label: "Over-capacity re-alert delta", desc: "how much worse before an acked signal returns",       unit: "pts",    min: 1 },
  { key: "signal_rearm_days",          label: "Signal re-arm window",       desc: "acked condition signals return if still true after…",   unit: "days",   min: 1 },
];

const WEIGHT_META = [
  { key: "deadline",        label: "Deadline" },
  { key: "consequence",     label: "Consequence" },
  { key: "reversibility",   label: "Reversibility" },
  { key: "blocking",        label: "Blocking" },
  { key: "data_confidence", label: "Data confidence" },
];

// ─── Normalization helper ─────────────────────────────────────────────────────

/**
 * When one weight slider moves, proportionally re-normalise the other four
 * so the total stays 100. If all others are 0, split the remainder evenly.
 */
function normalizeWeights(weights, changedKey, newValue) {
  const val = Math.max(0, Math.min(100, Math.round(newValue)));
  const otherKeys = Object.keys(weights).filter((k) => k !== changedKey);
  const otherSum  = otherKeys.reduce((s, k) => s + weights[k], 0);
  const remaining = 100 - val;

  if (remaining <= 0) {
    const result = { [changedKey]: 100 };
    for (const k of otherKeys) result[k] = 0;
    return result;
  }

  if (otherSum === 0) {
    const even  = Math.floor(remaining / otherKeys.length);
    const carry = remaining - even * otherKeys.length;
    const result = { [changedKey]: val };
    for (let i = 0; i < otherKeys.length; i++) {
      result[otherKeys[i]] = even + (i === 0 ? carry : 0);
    }
    return result;
  }

  const scale  = remaining / otherSum;
  const result = { [changedKey]: val };
  let distributed = 0;
  for (let i = 0; i < otherKeys.length - 1; i++) {
    result[otherKeys[i]] = Math.round(weights[otherKeys[i]] * scale);
    distributed += result[otherKeys[i]];
  }
  result[otherKeys[otherKeys.length - 1]] = remaining - distributed;
  return result;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const CARD_STYLE = {
  background:   T.white,
  border:       `1px solid ${T.border}`,
  borderRadius: 12,
  padding:      "15px 17px",
  marginBottom: 14,
};

const CARD_TITLE_STYLE = {
  display:      "flex",
  alignItems:   "center",
  gap:          9,
  fontWeight:   T.fwBold,
  fontSize:     T.fsMd,
  marginBottom: 3,
};

const CARD_TITLE_BAR = {
  width:        3,
  height:       15,
  borderRadius: 2,
  background:   T.blue,
  flexShrink:   0,
};

const CARD_DESC_STYLE = {
  fontSize:    T.fsSm5,
  color:       T.mid,
  marginBottom: 12,
};

const ROW_BASE = {
  display:      "flex",
  alignItems:   "center",
  gap:          12,
  padding:      "9px 0",
  borderTop:    `1px solid ${T.lightGray}`,
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function PillBadge({ status }) {
  const PILL_STYLES = {
    live:     { background: T.greenSoft, color: T.greenDeep },
    gated:    { background: T.pillGatedBg,    color: T.pillGatedFg    },
    deferred: { background: T.pillDeferredBg, color: T.pillDeferredFg },
  };
  const PILL_LABELS = { live: "Live", gated: "Trust-gated", deferred: "Deferred" };
  const s = PILL_STYLES[status] ?? PILL_STYLES.live;
  return (
    <span style={{
      fontSize:      T.fsXxxs,
      fontWeight:    T.fwBold,
      letterSpacing: ".03em",
      textTransform: "uppercase",
      padding:       "2px 7px",
      borderRadius:  5,
      ...s,
    }}>
      {PILL_LABELS[status]}
    </span>
  );
}

function Toggle({ on, onChange, locked, testId }) {
  const bg     = locked ? T.toggleLockedBg : on ? T.blue : T.borderDark;
  const thumbX = locked ? 2 : on ? "right" : 2;
  return (
    <button
      type="button"
      data-testid={testId}
      role="switch"
      aria-checked={on}
      disabled={locked}
      onClick={locked ? undefined : onChange}
      style={{
        position:  "relative",
        width:     38,
        height:    21,
        borderRadius: 999,
        background: bg,
        border:    "none",
        padding:   0,
        cursor:    locked ? "not-allowed" : "pointer",
        flexShrink: 0,
        outline:   "none",
      }}
    >
      <span style={{
        position:     "absolute",
        top:          2,
        left:         locked ? 2 : on ? "auto" : 2,
        right:        (!locked && on) ? 2 : "auto",
        width:        17,
        height:       17,
        borderRadius: "50%",
        background:   locked ? T.toggleLockedThumb : T.white,
        transition:   "left 0.12s, right 0.12s",
      }} />
    </button>
  );
}

function StepperControl({ value, unit, onDecrement, onIncrement, testIdPrefix }) {
  return (
    <div
      data-testid={`${testIdPrefix}-stepper`}
      style={{
        display:      "inline-flex",
        alignItems:   "center",
        border:       `1px solid ${T.border}`,
        borderRadius: 9,
        overflow:     "hidden",
        flexShrink:   0,
      }}
    >
      <button
        type="button"
        data-testid={`${testIdPrefix}-decrement`}
        onClick={onDecrement}
        style={{ border: "none", background: T.lightGray, width: 28, height: 30, fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.mid, cursor: "pointer" }}
      >
        −
      </button>
      <span
        data-testid={`${testIdPrefix}-value`}
        style={{ minWidth: 74, textAlign: "center", fontWeight: T.fwBold, fontSize: T.fsSm5, padding: "0 4px" }}
      >
        {unit === "pts" ? `+${value} pts` : `${value} ${unit}`}
      </span>
      <button
        type="button"
        data-testid={`${testIdPrefix}-increment`}
        onClick={onIncrement}
        style={{ border: "none", background: T.lightGray, width: 28, height: 30, fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.mid, cursor: "pointer" }}
      >
        +
      </button>
    </div>
  );
}

function WeightSlider({ label, weightKey, value, onChange }) {
  const trackRef = useRef(null);

  function handleTrackClick(e) {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const pct  = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onChange(Math.round(pct * 100));
  }

  return (
    <div data-testid={`weight-row-${weightKey}`} style={{ display: "flex", alignItems: "center", gap: 12, padding: "9px 0" }}>
      <span style={{ width: 130, fontWeight: T.fwBold, fontSize: T.fsSm5 }}>{label}</span>
      <div
        ref={trackRef}
        data-testid={`weight-track-${weightKey}`}
        onClick={handleTrackClick}
        style={{ flex: 1, height: 8, borderRadius: 5, background: T.bg, position: "relative", cursor: "pointer" }}
      >
        <span style={{ display: "block", height: "100%", borderRadius: 5, background: T.blue, width: `${value}%` }} />
        <span style={{
          position:  "absolute",
          top:       -3,
          left:      `${value}%`,
          transform: "translateX(-50%)",
          width:     14,
          height:    14,
          borderRadius: "50%",
          background: T.white,
          border:    `2px solid ${T.blue}`,
        }} />
      </div>
      <input
        type="range"
        data-testid={`weight-slider-${weightKey}`}
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 0, height: 0 }}
        aria-label={label}
      />
      <span data-testid={`weight-value-${weightKey}`} style={{ width: 44, textAlign: "right", fontWeight: T.fwBold, fontSize: T.fsSm5 }}>
        {value}
      </span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function RadarSignalNoisePanel() {
  const [config,   setConfig]   = useState(DEFAULT_CONFIG);
  const [original, setOriginal] = useState(DEFAULT_CONFIG);
  const [preview,  setPreview]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState(null);

  // ── Load settings on mount ────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [settings, feedResult] = await Promise.all([
          getRadarSettings(),
          getFocusItemsFeed().catch(() => null),
        ]);
        if (cancelled) return;
        const merged = {
          timing:         { ...DEFAULT_TIMING,           ...(settings.timing          ?? {}) },
          emitters:       { ...DEFAULT_EMITTERS,         ...(settings.emitters        ?? {}) },
          rankingWeights: { ...DEFAULT_RANKING_WEIGHTS,  ...(settings.rankingWeights  ?? {}) },
          bands:          { ...DEFAULT_BANDS,            ...(settings.bands           ?? {}) },
        };
        setConfig(merged);
        setOriginal(merged);
        if (feedResult?.counts) setPreview(feedResult.counts);
      } catch (err) {
        if (!cancelled) setError(err.message ?? "Failed to load radar settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Save changes ──────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      // Build diff — only send groups that changed
      const diff = {};
      if (JSON.stringify(config.timing)         !== JSON.stringify(original.timing))         diff.timing         = config.timing;
      if (JSON.stringify(config.emitters)       !== JSON.stringify(original.emitters))       diff.emitters       = config.emitters;
      if (JSON.stringify(config.rankingWeights) !== JSON.stringify(original.rankingWeights)) diff.rankingWeights = config.rankingWeights;
      if (JSON.stringify(config.bands)          !== JSON.stringify(original.bands))          diff.bands          = config.bands;

      if (Object.keys(diff).length === 0) {
        showToast("No changes to save.", { type: "info" });
        return;
      }

      await patchRadarSettings(diff);
      setOriginal(config);
      showToast("Radar settings saved.", { type: "success" });

      // Re-fetch preview banner counts after save
      try {
        const feedResult = await getFocusItemsFeed();
        if (feedResult?.counts) setPreview(feedResult.counts);
      } catch (_) { /* best-effort */ }
    } catch (err) {
      log.error("RadarSignalNoisePanel save error", err);
      setError(err.message ?? "Failed to save settings");
    } finally {
      setSaving(false);
    }
  }, [config, original]);

  // ── Reset to defaults ─────────────────────────────────────────────────────
  const handleReset = useCallback(() => {
    setConfig(DEFAULT_CONFIG);
  }, []);

  // ── Timing stepper helpers ────────────────────────────────────────────────
  const stepTiming = useCallback((key, meta, delta) => {
    setConfig((prev) => ({
      ...prev,
      timing: {
        ...prev.timing,
        [key]: Math.max(meta.min, prev.timing[key] + delta),
      },
    }));
  }, []);

  // ── Weight slider helper ──────────────────────────────────────────────────
  const handleWeightChange = useCallback((key, newVal) => {
    setConfig((prev) => ({
      ...prev,
      rankingWeights: normalizeWeights(prev.rankingWeights, key, newVal),
    }));
  }, []);

  // ── Band stepper helpers ──────────────────────────────────────────────────
  const stepBand = useCallback((key, delta) => {
    setConfig((prev) => ({
      ...prev,
      bands: {
        ...prev.bands,
        [key]: Math.max(1, prev.bands[key] + delta),
      },
    }));
  }, []);

  if (loading) {
    return (
      <div data-testid="radar-signal-noise-panel" style={{ padding: 24, color: T.mid }}>
        Loading radar settings…
      </div>
    );
  }

  const surfacedCount = preview
    ? (preview.act_now ?? 0) + (preview.this_cycle ?? 0) + (preview.keep_watch ?? 0)
    : null;
  const inFocusCount  = preview ? (preview.act_now ?? 0) + (preview.this_cycle ?? 0) : null;
  const keepWatchCount = preview ? (preview.keep_watch ?? 0) : null;
  const snoozedCount  = preview ? (preview.snoozed ?? 0) : null;

  return (
    <div data-testid="radar-signal-noise-panel">
      <h2 style={{ fontSize: T.fsLg5, fontWeight: T.fwBold, margin: "0 0 2px" }}>
        Radar — signal &amp; noise
      </h2>
      <p style={{ fontSize: T.fsSm5, color: T.mid, margin: "0 0 16px" }}>
        Control what surfaces, how loud, and how it's ranked. Everything here is audited; changes take effect on the next emit cycle.
      </p>

      {error && (
        <div
          data-testid="radar-settings-error"
          style={{ background: T.redSoft, border: `1px solid ${T.redLight}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12, color: T.red, fontSize: T.fsSm5 }}
        >
          {error}
        </div>
      )}

      {/* ── Live preview banner ─────────────────────────────────────────────── */}
      <div
        data-testid="radar-preview-banner"
        style={{
          display:      "flex",
          alignItems:   "center",
          gap:          14,
          background:   T.tooltip.bg,
          color:        T.white,
          borderRadius: 12,
          padding:      "13px 16px",
          marginBottom: 16,
        }}
      >
        <div>
          <div data-testid="preview-surfaced" style={{ fontSize: T.fsLgX, fontWeight: T.fwDisplay }}>{surfacedCount ?? "—"}</div>
          <div style={{ fontSize: T.fsXs, color: T.radarBannerFg }}>surfaced</div>
        </div>
        <div style={{ width: 1, height: 30, background: "rgba(255,255,255,.18)" }} />
        <div>
          <div data-testid="preview-in-focus" style={{ fontSize: T.fsLgX, fontWeight: T.fwDisplay }}>{inFocusCount ?? "—"}</div>
          <div style={{ fontSize: T.fsXs, color: T.radarBannerFg }}>in focus</div>
        </div>
        <div style={{ width: 1, height: 30, background: "rgba(255,255,255,.18)" }} />
        <div>
          <div data-testid="preview-keep-watch" style={{ fontSize: T.fsLgX, fontWeight: T.fwDisplay }}>{keepWatchCount ?? "—"}</div>
          <div style={{ fontSize: T.fsXs, color: T.radarBannerFg }}>keep watch</div>
        </div>
        <div style={{ width: 1, height: 30, background: "rgba(255,255,255,.18)" }} />
        <div>
          <div data-testid="preview-snoozed" style={{ fontSize: T.fsLgX, fontWeight: T.fwDisplay }}>{snoozedCount ?? "—"}</div>
          <div style={{ fontSize: T.fsXs, color: T.radarBannerFg }}>muted / snoozed</div>
        </div>
        <div style={{ marginLeft: "auto", fontSize: T.fsXs5, color: T.radarBannerFg }}>
          Live preview of current settings
        </div>
      </div>

      {/* ── Emitters card ───────────────────────────────────────────────────── */}
      <div data-testid="emitters-card" style={CARD_STYLE}>
        <div style={CARD_TITLE_STYLE}>
          <span style={CARD_TITLE_BAR} />
          Emitters
        </div>
        <div style={CARD_DESC_STYLE}>
          Which rules are allowed to put items on Radar. Trust-gated ones unlock only when their data is trusted.
        </div>
        {EMITTER_META.map((em, idx) => {
          const isLocked = em.status !== "live";
          const isOn     = config.emitters[em.key] ?? false;
          return (
            <div
              key={em.key}
              data-testid={`emitter-row-${em.key}`}
              style={{ ...ROW_BASE, borderTop: idx === 0 ? "none" : ROW_BASE.borderTop }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: T.fwBold, fontSize: T.fsSm5 }}>{em.label}</div>
                <div style={{ fontSize: T.fsXs, color: T.light }}>{em.desc}</div>
              </div>
              <PillBadge status={em.status} />
              <Toggle
                on={isOn}
                locked={isLocked}
                testId={`emitter-toggle-${em.key}`}
                onChange={() => setConfig((prev) => ({
                  ...prev,
                  emitters: { ...prev.emitters, [em.key]: !prev.emitters[em.key] },
                }))}
              />
            </div>
          );
        })}
      </div>

      {/* ── Timing & return dials card ──────────────────────────────────────── */}
      <div data-testid="timing-card" style={CARD_STYLE}>
        <div style={CARD_TITLE_STYLE}>
          <span style={CARD_TITLE_BAR} />
          Timing &amp; return dials
        </div>
        <div style={CARD_DESC_STYLE}>
          How early items appear and when acknowledged/condition items come back.
        </div>
        {TIMING_META.map((tm, idx) => (
          <div
            key={tm.key}
            data-testid={`timing-row-${tm.key}`}
            style={{ ...ROW_BASE, borderTop: idx === 0 ? "none" : ROW_BASE.borderTop }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: T.fwBold, fontSize: T.fsSm5 }}>{tm.label}</div>
              <div style={{ fontSize: T.fsXs, color: T.light }}>{tm.desc}</div>
            </div>
            <StepperControl
              value={config.timing[tm.key]}
              unit={tm.unit}
              testIdPrefix={`timing-${tm.key}`}
              onDecrement={() => stepTiming(tm.key, tm, -1)}
              onIncrement={() => stepTiming(tm.key, tm, +1)}
            />
          </div>
        ))}
      </div>

      {/* ── Ranking weights card ─────────────────────────────────────────────── */}
      <div data-testid="ranking-weights-card" style={CARD_STYLE}>
        <div style={CARD_TITLE_STYLE}>
          <span style={CARD_TITLE_BAR} />
          Ranking weights
        </div>
        <div style={CARD_DESC_STYLE}>
          How much each factor contributes to an item's priority (and whether it makes "in focus").
        </div>
        {WEIGHT_META.map((wm) => (
          <WeightSlider
            key={wm.key}
            label={wm.label}
            weightKey={wm.key}
            value={config.rankingWeights[wm.key]}
            onChange={(v) => handleWeightChange(wm.key, v)}
          />
        ))}
        <p style={{ fontSize: T.fsXs, color: T.light, marginTop: 8 }}>
          Weights are relative (normalized to 100). Every rank stays explainable — the item panel shows this exact breakdown.
        </p>
      </div>

      {/* ── Bands & confidence gate card ────────────────────────────────────── */}
      <div data-testid="bands-card" style={CARD_STYLE}>
        <div style={CARD_TITLE_STYLE}>
          <span style={CARD_TITLE_BAR} />
          Bands &amp; confidence gate
        </div>
        <div style={CARD_DESC_STYLE}>
          Where the ranking splits into the three bands, and how low-trust items are held back.
        </div>

        {/* Act-now row */}
        <div data-testid="band-row-act_now" style={{ ...ROW_BASE, borderTop: "none" }}>
          <span style={{ width: 110, fontWeight: T.fwBold, fontSize: T.fsSm5 }}>Act now</span>
          <span style={{ fontSize: T.fsSm5, color: T.mid }}>overdue, or due within</span>
          <StepperControl
            value={config.bands.act_now_days}
            unit="days"
            testIdPrefix="band-act-now"
            onDecrement={() => stepBand("act_now_days", -1)}
            onIncrement={() => stepBand("act_now_days", +1)}
          />
        </div>

        {/* This-cycle row */}
        <div data-testid="band-row-this_cycle" style={ROW_BASE}>
          <span style={{ width: 110, fontWeight: T.fwBold, fontSize: T.fsSm5 }}>This cycle</span>
          <span style={{ fontSize: T.fsSm5, color: T.mid }}>due within</span>
          <StepperControl
            value={config.bands.this_cycle_days}
            unit="days"
            testIdPrefix="band-this-cycle"
            onDecrement={() => stepBand("this_cycle_days", -1)}
            onIncrement={() => stepBand("this_cycle_days", +1)}
          />
        </div>

        {/* Keep watch static row */}
        <div data-testid="band-row-keep_watch" style={ROW_BASE}>
          <span style={{ width: 110, fontWeight: T.fwBold, fontSize: T.fsSm5 }}>Keep watch</span>
          <span style={{ fontSize: T.fsSm5, color: T.mid }}>everything else (no date / monitoring)</span>
        </div>

        {/* Low-confidence cap row */}
        <div data-testid="band-row-low_confidence" style={ROW_BASE}>
          <span style={{ width: 110, fontWeight: T.fwBold, fontSize: T.fsSm5 }}>Low confidence</span>
          <Toggle
            on={config.bands.low_confidence_cap}
            locked={false}
            testId="band-low-confidence-toggle"
            onChange={() => setConfig((prev) => ({
              ...prev,
              bands: { ...prev.bands, low_confidence_cap: !prev.bands.low_confidence_cap },
            }))}
          />
          <span style={{ fontSize: T.fsSm5, color: T.mid }}>
            cap low-confidence items at This-cycle (never Act-now) and label them "low confidence"
          </span>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 9, marginTop: 6 }}>
        <button
          type="button"
          data-testid="radar-save-btn"
          disabled={saving}
          onClick={handleSave}
          style={{
            background:   T.tooltip.bg,
            color:        T.white,
            border:       "none",
            borderRadius: 9,
            padding:      "8px 13px",
            fontWeight:   T.fwBold,
            fontSize:     T.fsSm5,
            cursor:       saving ? "not-allowed" : "pointer",
            opacity:      saving ? 0.7 : 1,
          }}
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          type="button"
          data-testid="radar-reset-btn"
          disabled={saving}
          onClick={handleReset}
          style={{
            border:       `1px solid ${T.border}`,
            background:   T.white,
            borderRadius: 9,
            padding:      "8px 13px",
            fontWeight:   T.fwBold,
            fontSize:     T.fsSm5,
            cursor:       "pointer",
            color:        T.mid,
          }}
        >
          Reset to defaults
        </button>
      </div>
    </div>
  );
}
